// Beranda, the dashboard tab.
//
// Round 7 calmed this screen down. The four number tiles are gone: four
// digits in a row with nothing attached to them is a wall, not information.
// What leads instead is a banner built from the traveller's OWN content,
// their latest entry or a destination they saved, with a real photo. Under
// it, at most two numbers, and only while a trip is actually running, so a
// figure only appears when there is something to act on.
//
// The banner is a photo with text on a flat surface. The text sits OUTSIDE
// the image, never over it, so there is no overlay, no scrim, and therefore
// no gradient hiding inside a scrim.
import { useEffect, useMemo, useState } from "react";
import {
  Icon,
  IconBook,
  IconBookmark,
  IconCalendar,
  IconCamera,
  IconChart,
  IconChevronRight,
  IconFeed,
  IconPlan,
  IconRoute,
  IconWallet,
} from "@traveldiary/ui";
import type { ReactNode } from "react";
import { MomentDetail } from "../components/MomentDetail";
import { MomentRow } from "../components/MomentRow";
import { Sheet } from "../components/Sheet";
import { TravellerTimeline } from "../components/TravellerTimeline";
import { apiClient } from "../lib/api";
import { formatDateShort, formatNumber } from "../lib/format";
import { hrefFor, navigate, type RouteName } from "../lib/router";
import type { AuthoredMoment, Moment, PublicUser } from "../lib/types";
import type { TranslationKey } from "../lib/i18n";
import { useApp } from "../state/app";

interface Shortcut {
  route: RouteName;
  param?: string;
  labelKey: TranslationKey;
  glyph: ReactNode;
}

/**
 * Eight, deliberately: it is the exact count that fills both the 2 column
 * phone grid and the 4 column tablet grid with no leftover cell. Capture is
 * first because it is the write later entry point, the other one being the
 * floating control on the map.
 */
const SHORTCUTS: Shortcut[] = [
  { route: "capture", labelKey: "capture.title", glyph: <IconCamera /> },
  { route: "diary", labelKey: "diary.title", glyph: <IconBook /> },
  { route: "trips", labelKey: "trips.title", glyph: <IconRoute /> },
  { route: "itinerary", labelKey: "itinerary.title", glyph: <IconPlan /> },
  { route: "diary", param: "calendar", labelKey: "calendar.title", glyph: <IconCalendar /> },
  { route: "budget", labelKey: "budget.title", glyph: <IconWallet /> },
  { route: "stats", labelKey: "stats.title", glyph: <IconChart /> },
  { route: "wishlist", labelKey: "wishlist.title", glyph: <IconBookmark /> },
];

type HomeSheet =
  | { kind: "entry"; entry: AuthoredMoment }
  | { kind: "author"; user: PublicUser };

/** What the banner is showing, and what tapping it does. */
type Banner =
  | { kind: "entry"; photo: string | null; title: string; meta: string }
  | { kind: "wish"; photo: string | null; title: string; meta: string }
  | { kind: "start" };

function firstPhoto(media: Moment["media"]): string | null {
  const image = media.find((item) => !item.mime.startsWith("video/")) ?? media[0];
  return image ? apiClient.mediaUrl(image.storageKey) : null;
}

export function HomeScreen() {
  const {
    t,
    locale,
    me,
    activeTrip,
    moments,
    pendingMoments,
    wishlist,
    exploreMoments,
    refreshExplore,
  } = useApp();
  const [sheet, setSheet] = useState<HomeSheet | null>(null);

  useEffect(() => {
    void refreshExplore();
  }, [refreshExplore]);

  /**
   * The traveller's own content first, in the order they would expect to see
   * it: the newest entry with a photo from the trip they are on, then their
   * newest published entry anywhere, then something they saved. Only when
   * they have written nothing at all does this become an invitation.
   */
  const banner = useMemo<Banner>(() => {
    const withPhoto = [...moments]
      .filter((moment) => moment.media.length > 0)
      .sort((a, b) => b.capturedAt.localeCompare(a.capturedAt))[0];
    const ownPublic = exploreMoments
      .filter((entry) => entry.author?.id && entry.author.id === me?.id && entry.media.length > 0)
      .sort((a, b) => b.capturedAt.localeCompare(a.capturedAt))[0];
    const entry = withPhoto ?? ownPublic;
    if (entry) {
      return {
        kind: "entry",
        photo: firstPhoto(entry.media),
        title: entry.placeName || entry.caption || t("home.bannerLatest"),
        meta: `${t("home.bannerLatest")}. ${formatDateShort(entry.capturedAt, locale)}`,
      };
    }
    const saved = wishlist.find((item) => item.media.length > 0) ?? wishlist[0];
    if (saved) {
      return {
        kind: "wish",
        photo: saved.media[0] ? apiClient.mediaUrl(saved.media[0].storageKey) : null,
        title: saved.name,
        meta: t("home.bannerSaved"),
      };
    }
    return { kind: "start" };
  }, [moments, exploreMoments, wishlist, me?.id, locale, t]);

  /**
   * Two numbers, both about the trip in progress, both actionable: how much
   * is written, and how many days it covers. Nothing is shown at all when no
   * trip is running, because a zero with no trip behind it says nothing.
   */
  const tripNumbers = useMemo(() => {
    if (!activeTrip) return null;
    const own = moments.filter((moment) => moment.tripId === activeTrip.id);
    const queued = pendingMoments.filter((item) => item.tripId === activeTrip.id);
    const days = new Set(own.map((moment) => moment.capturedAt.slice(0, 10)));
    return { entries: own.length + queued.length, days: days.size };
  }, [activeTrip, moments, pendingMoments]);

  const recent = exploreMoments.slice(0, 3);

  return (
    <div className="screen-pad">
      <section className="section">
        {banner.kind === "start" ? (
          <a className="banner banner-empty" href={hrefFor("capture")}>
            <span className="banner-body">
              <span className="banner-title">{t("home.bannerStart")}</span>
              <span className="banner-meta">{t("home.bannerStartHint")}</span>
              <span className="banner-cta">
                {t("home.bannerCapture")}
                <Icon size={16}>
                  <IconChevronRight />
                </Icon>
              </span>
            </span>
          </a>
        ) : (
          <button
            type="button"
            className="banner"
            onClick={() => navigate(banner.kind === "entry" ? "diary" : "wishlist")}
          >
            <span className="banner-media">
              {banner.photo ? <img className="banner-photo" src={banner.photo} alt="" /> : null}
            </span>
            <span className="banner-body">
              <span className="banner-title">{banner.title}</span>
              <span className="banner-meta">{banner.meta}</span>
              <span className="banner-cta">
                {t("home.bannerOpen")}
                <Icon size={16}>
                  <IconChevronRight />
                </Icon>
              </span>
            </span>
          </button>
        )}

        {activeTrip ? (
          <div className="trip-now">
            <p className="trip-now-title">{activeTrip.title}</p>
            <p className="trip-now-dates">
              {formatDateShort(activeTrip.startDate, locale)}
              {activeTrip.endDate
                ? ` ${t("trips.until")} ${formatDateShort(activeTrip.endDate, locale)}`
                : ""}
            </p>
            {tripNumbers ? (
              <div className="quiet-stats">
                <span className="quiet-stat">
                  <span className="quiet-value">{formatNumber(tripNumbers.entries, locale)}</span>
                  <span className="quiet-label">{t("home.tripEntries")}</span>
                </span>
                <span className="quiet-stat">
                  <span className="quiet-value">{formatNumber(tripNumbers.days, locale)}</span>
                  <span className="quiet-label">{t("home.tripDays")}</span>
                </span>
              </div>
            ) : null}
          </div>
        ) : null}
      </section>

      <section className="section">
        <h2 className="section-title">{t("home.features")}</h2>
        <div className="tile-grid tile-grid-shortcuts">
          {SHORTCUTS.map((shortcut) => (
            <a
              key={`${shortcut.route}-${shortcut.param ?? ""}`}
              className="tile tile-action"
              href={hrefFor(shortcut.route, shortcut.param)}
            >
              <Icon size={22}>{shortcut.glyph}</Icon>
              <span className="tile-label">{t(shortcut.labelKey)}</span>
            </a>
          ))}
        </div>
      </section>

      <section className="section">
        <h2 className="section-title">{t("home.activity")}</h2>
        {recent.length === 0 ? (
          <p className="section-sub">{t("home.activityEmptyHint")}</p>
        ) : (
          <div className="pub-feed">
            {recent.map((entry) => (
              <MomentRow
                key={entry.id}
                entry={entry}
                onOpenAuthor={(row) => setSheet({ kind: "author", user: row.author })}
                onOpenDetail={(row) => setSheet({ kind: "entry", entry: row })}
              />
            ))}
          </div>
        )}
        <button
          type="button"
          className="btn btn-quiet btn-block"
          onClick={() => navigate("timeline")}
        >
          <Icon size={18}>
            <IconFeed />
          </Icon>
          {t("home.activityAll")}
          <Icon size={16}>
            <IconChevronRight />
          </Icon>
        </button>
      </section>

      <Sheet
        open={Boolean(sheet)}
        title={
          sheet?.kind === "author"
            ? sheet.user.displayName
            : sheet?.kind === "entry"
              ? sheet.entry.placeName || t("explore.noPlace")
              : t("home.activity")
        }
        onClose={() => setSheet(null)}
      >
        {sheet?.kind === "author" ? (
          <TravellerTimeline
            user={sheet.user}
            onOpenMoment={(entry) => setSheet({ kind: "entry", entry })}
          />
        ) : null}
        {sheet?.kind === "entry" ? (
          <MomentDetail
            entry={sheet.entry}
            onOpenTimeline={(user) => setSheet({ kind: "author", user })}
          />
        ) : null}
      </Sheet>
    </div>
  );
}
